import { createContext, useContext, useEffect, useState } from 'react';
import { endpoints } from '@/api/client.js';
import { CONFERENCE_DATES_DEFAULTS } from '@/data/conferenceDates.js';

/**
 * App-wide provider for the admin-editable conference dates.
 *
 * It performs a single fetch of the `settings` content block at startup and
 * merges any stored `conferenceDates` override over the built-in defaults, so
 * every component that calls {@link useConferenceDates} stays in sync with the
 * admin panel without each one fetching on its own.
 *
 * The context defaults to the built-in dates, so components still render
 * correctly before the fetch resolves or when used outside the provider.
 */
const ConferenceConfigContext = createContext(CONFERENCE_DATES_DEFAULTS);

export function ConferenceConfigProvider({ children }) {
  const [dates, setDates] = useState(CONFERENCE_DATES_DEFAULTS);

  useEffect(() => {
    endpoints
      .contentBlock('settings')
      .then((res) => {
        const value = res?.data?.data?.value ?? res?.data?.value ?? null;
        const stored = value?.conferenceDates;
        if (stored && typeof stored === 'object') {
          setDates((prev) => ({ ...prev, ...stored }));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <ConferenceConfigContext.Provider value={dates}>
      {children}
    </ConferenceConfigContext.Provider>
  );
}

/** Read the current (possibly admin-overridden) conference dates. */
export function useConferenceDates() {
  return useContext(ConferenceConfigContext);
}
